/**
 * DynamicFormField Component
 *
 * Renders a form field based on its type definition.
 * Supports: text, email, number, textarea, select, checkbox, date
 *
 * Requirements: Task 12 - Forms Pages
 */

import { Input, Textarea } from '@bloomcode/ui';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';

export interface FormFieldDefinition {
  name: string;
  label: string;
  type: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox' | 'date';
  required?: boolean;
  options?: string[];
  placeholder?: string;
}

interface DynamicFormFieldProps {
  field: FormFieldDefinition;
  register: UseFormRegister<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
}

export function DynamicFormField({ field, register, errors }: DynamicFormFieldProps) {
  const fieldError = errors[field.name];
  const errorMessage = fieldError?.message as string | undefined;
  const hasError = !!fieldError;

  const commonProps = {
    id: field.name,
    'aria-invalid': hasError || undefined,
    'aria-describedby': hasError ? `${field.name}-error` : undefined,
  };

  return (
    <div className="space-y-1.5">
      {field.type !== 'checkbox' && (
        <label htmlFor={field.name} className="text-sm font-medium text-foreground">
          {field.label}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      {/* Text input */}
      {field.type === 'text' && (
        <Input
          {...commonProps}
          type="text"
          placeholder={field.placeholder ?? ''}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      )}

      {/* Email input */}
      {field.type === 'email' && (
        <Input
          {...commonProps}
          type="email"
          placeholder={field.placeholder ?? 'email@example.com'}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Please enter a valid email address',
            },
          })}
        />
      )}

      {/* Number input */}
      {field.type === 'number' && (
        <Input
          {...commonProps}
          type="number"
          placeholder={field.placeholder ?? '0'}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
            valueAsNumber: true,
          })}
        />
      )}

      {/* Date input */}
      {field.type === 'date' && (
        <Input
          {...commonProps}
          type="date"
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      )}

      {/* Textarea */}
      {field.type === 'textarea' && (
        <Textarea
          {...commonProps}
          placeholder={field.placeholder ?? ''}
          rows={4}
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        />
      )}

      {/* Select */}
      {field.type === 'select' && (
        <select
          {...commonProps}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          {...register(field.name, {
            required: field.required ? `${field.label} is required` : false,
          })}
        >
          <option value="">{field.placeholder ?? `Select ${field.label.toLowerCase()}`}</option>
          {field.options?.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      )}

      {/* Checkbox */}
      {field.type === 'checkbox' && (
        <div className="flex items-center gap-2">
          <input
            {...commonProps}
            type="checkbox"
            className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
          />
          <label htmlFor={field.name} className="text-sm font-medium text-foreground">
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </label>
        </div>
      )}

      {/* Error message */}
      {hasError && errorMessage && (
        <p id={`${field.name}-error`} className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default DynamicFormField;
